import { Button, ButtonBase, Grid, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EjectIcon from "@mui/icons-material/Eject";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrintIcon from "@mui/icons-material/Print";
import PublishIcon from "@mui/icons-material/Publish";
import ReplyIcon from "@mui/icons-material/Reply";
import TuneIcon from "@mui/icons-material/Tune";
import { observer } from "mobx-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { AuthStore } from "stores/AuthStore";
import { VizType } from "../../../models/Reporting/ReportModel";
import theme from "../../../Theme/AppTheme";
import { delay } from "../../../utils/helpers";
import { useStore } from "../../../utils/useStore";
import MessageStore from "../../ManagerInteractions/Stores/MessageStore";
import AcxMenu, { AcxMenuItemProps } from "../../UI/Menu/AcxMenu";
import ReportsStore from "./Stores/ReportsStore";
import { styled } from "@mui/material/styles";

const StyledMenuText = styled(Typography)(({ theme }) => ({
    fontSize: "13px",
    letterSpacing: "0",
    lineHeight: "20px",
    color: "inherit",
}));

const StyledMenuTextImportant = styled(StyledMenuText)(({ theme }) => ({
    color: theme.palette.red.main,
    "&:hover, &:focus": {
        color: theme.palette.neutral[600],
    },
}));

interface Props {
    reportName: string;
    editedBy: string;
    editedOn: string;
    editedAt: string;
    domain: "Signals" | "Reports" | "ReportsRv2";
}

const ReportHeader = observer((props: Props) => {
    const store = useStore(ReportsStore);
    const authStore = useStore(AuthStore);
    const msgStore = useStore(MessageStore);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const navigate = useNavigate();

    function onEditReport() {
        store.initializeReportEditorForExisting();
        if (
            store.orgStore.selectedOrganization?.id &&
            store.currentReport?.id
        ) {
            if (props.domain === "Signals") {
                navigate(
                    `/focus/signals/reporteditor/${store.orgStore.selectedOrganization.id}/${store.currentReport.id}`,
                );
            } else if (props.domain === "Reports") {
                navigate(
                    `/focus/reporteditor/${store.orgStore.selectedOrganization.id}/${store.currentReport.id}`,
                );
            } else if (props.domain === "ReportsRv2") {
                navigate(
                    `/RV2/focus/reporteditor/${store.orgStore.selectedOrganization.id}/${store.currentReport.id}`,
                );
            }
        }
    }

    function handleMoreMenuClick(event: React.MouseEvent<HTMLButtonElement>) {
        setAnchorEl(event.currentTarget);
    }

    function handleMoreMenuClose() {
        setAnchorEl(null);
    }

    function handleSharePublicLink() {
        handleMoreMenuClose();
    }

    function handleOpenChartConfig() {
        handleMoreMenuClose();
        delay(25).then(store.populateChartConfigDrawer);
    }

    function handleDeleteReport() {
        store.deleteReport();
        handleMoreMenuClose();
    }

    function handlePublishAsWidget() {
        store.publishReportAsWidget();
        handleMoreMenuClose();
    }

    function handleUnPublishAsWidget() {
        store.unPublishReportWidget();
        handleMoreMenuClose();
    }

    function onDownloadChartImg() {
        const imgURI = store.chartWrapper?.getChart()?.getImageURI();
        if (imgURI && store.currentReport) {
            const a = document.createElement("a");
            a.setAttribute("style", "display:none");
            a.id = "AcxVizPng";
            a.style.display = "none";
            a.href = imgURI;
            a.download = `${store.currentReport.name.trim()}.png`;
            a.click();
            a.remove();
        } else {
            msgStore.logWarning("Unable to download chart as .png");
        }

        handleMoreMenuClose();
    }

    const menuItems: AcxMenuItemProps[] = [
        {
            id: "share-report-menu-item",
            hidden: true,
            props: { onClick: handleSharePublicLink, disabled: true },
            label: <StyledMenuText>Share Public Link</StyledMenuText>,
            icon: <ReplyIcon sx={{ color: theme.palette.text.disabled }} />,
        },
        {
            id: "config-chart-report-menu-item",
            hidden: !store.currentReport,
            props: {
                onClick: handleOpenChartConfig,
                // disabled: store.currentReport?.vizType === VizType.Table,
            },
            label: <StyledMenuText>Chart Configuration</StyledMenuText>,
            icon: <TuneIcon color={"secondary"} />,
        },
        {
            id: "publish-report-as-widget",
            hidden: !authStore.canUserEdit("Widgets"),
            props: {
                onClick: handlePublishAsWidget,
                disabled:
                    !Boolean(store.currentReport?.id) || !store.canEditReports,
            },
            label: <StyledMenuText>Publish As Widget</StyledMenuText>,
            icon: <PublishIcon color={"secondary"} />,
        },
        {
            id: "un-publish-report-as-widget",
            hidden:
                !store.currentReport?.isPublished ||
                !authStore.canUserEdit("Widgets"),
            props: {
                onClick: handleUnPublishAsWidget,
                disabled:
                    !Boolean(store.currentReport?.id) || !store.canEditReports,
            },
            label: (
                <StyledMenuTextImportant>
                    UnPublish Widget
                </StyledMenuTextImportant>
            ),
            icon: <EjectIcon sx={{ color: theme.palette.red.main }} />,
        },
        {
            id: "delete-report-menu-item",
            props: {
                onClick: handleDeleteReport,
                disabled:
                    !Boolean(store.currentReport?.id) ||
                    !store.canEditReports ||
                    store.currentReport?.isPublished,
            },
            label: <StyledMenuTextImportant>Delete</StyledMenuTextImportant>,
            icon: <DeleteIcon sx={{ color: theme.palette.red.main }} />,
        },
        {
            id: "print-report-menu-item",
            props: {
                onClick: onDownloadChartImg,
                disabled:
                    !store.currentReport ||
                    !store.chartWrapper ||
                    store.currentReport?.vizType === VizType.Table,
            },
            label: <StyledMenuText>Download Chart</StyledMenuText>,
            icon: <PrintIcon />,
        },
    ];

    return (
        <>
            <Grid
                container
                sx={{
                    height: "88px",
                    width: "100%",
                    backgroundColor: theme.palette.white.main,
                    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
                    margin: 0,
                    boxShadow: theme.shadows[1],
                    zIndex: 10,
                }}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={9}>
                    <Grid container item spacing={2}>
                        <Grid item>
                            <Typography
                                sx={{
                                    fontSize: "20px",
                                    letterSpacing: "0",
                                    lineHeight: "28px",
                                }}
                                variant="subtitle1"
                            >
                                {props.reportName}
                            </Typography>
                        </Grid>
                        <Grid item style={{ alignSelf: "center" }}>
                            <BeatLoader
                                size={6}
                                color={theme.palette.secondary.main}
                                loading={
                                    store.reportDataViewModel.anyTaskLoading ||
                                    store.anyTaskLoading
                                }
                            />
                        </Grid>
                    </Grid>
                    {!store.currentReport?.drillThru && (
                        <Typography
                            sx={{
                                opacity: "0.5",
                                fontSize: "13px",
                                letterSpacing: "0",
                                lineHeight: "20px",
                                marginTop: theme.spacing(1),
                            }}
                            variant="h3"
                        >
                            Last edited by {props.editedBy} on {props.editedOn}{" "}
                            at {props.editedAt}{" "}
                        </Typography>
                    )}
                </Grid>
                <Grid
                    item
                    xs={3}
                    container
                    justifyContent={"flex-end"}
                    spacing={2}
                    wrap={"nowrap"}
                >
                    <Grid item>
                        <Button
                            onClick={onEditReport}
                            disabled={!store.canEditReports}
                            sx={{
                                textTransform: "none",
                                fontFamily: theme.typography.fontFamily,
                                fontWeight: theme.typography
                                    .fontWeightBold as any,
                                height: "32px",
                            }}
                            variant={"contained"}
                            color={"secondary"}
                            size={"small"}
                            disableElevation
                        >
                            Edit Criteria
                        </Button>
                    </Grid>
                    <Grid item>
                        <ButtonBase
                            onClick={handleMoreMenuClick}
                            sx={{
                                width: "32px",
                                height: "32px",
                                color: theme.palette.black.main,
                            }}
                        >
                            <MoreVertIcon />
                        </ButtonBase>
                    </Grid>
                </Grid>
            </Grid>

            <AcxMenu
                menuItems={menuItems}
                anchorElement={anchorEl}
                onMenuClose={handleMoreMenuClose}
            />
        </>
    );
});

export default ReportHeader;
